'use client'

import { useEffect, useRef, useState } from 'react'

interface FaqItem {
  question: string
  answer: string
}

/**
 * /services/ai-visibility-strategy -- the body, ported from
 * `public/radar-branding/ai-visibility-strategy.html`. See
 * `ai-visibility-strategy.css` for what the port changed and why.
 *
 * Client only for the mock's two behaviours: reveal-on-scroll (content is
 * VISIBLE by default and armed only once mounted, so SSR, failed hydration and
 * `prefers-reduced-motion` all show the finished page) and the FAQ show-more,
 * which the mock drove by toggling a `revealed` class and rewriting the label.
 *
 * The scroll-progress bar is dropped; the root layout renders `ScrollProgress`.
 */
/**
 * `heroPrice` comes from `getServicePriceModel()` in the route -- the same call
 * that builds the JSON-LD Offer. This string used to be hardcoded here, which
 * is exactly the drift that helper exists to prevent: the visible price and the
 * structured-data price have to move together.
 */
export default function AiVisibilityStrategyContent({
  faqItems,
  heroPrice,
}: {
  faqItems: readonly FaqItem[]
  heroPrice?: string | null
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [faqOpen, setFaqOpen] = useState(false)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const targets = Array.from(root.querySelectorAll('.avs-reveal'))
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(el => el.classList.add('avs-visible'))
      return
    }
    setEnhanced(true)
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('avs-visible')
            io.unobserve(e.target)
          }
        }),
      { threshold: 0.08 }
    )
    targets.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      className={`avs-page${enhanced ? ' avs-is-enhanced' : ''}`}
    >
      <section className='avs-hero' id='top'>
        <div className='avs-wrap'>
          <div className='avs-hero-grid'>
            <div className='avs-hero-copy avs-reveal avs-visible'>
              <p className='avs-label avs-hero-eyebrow'>
                Best for B2B sites with 10K+ organic visits and no AI citations
              </p>
              <h1 className='avs-display'>
                We make your site impossible for AI search to ignore.{' '}
                <span className='avs-pink'>Done for you.</span>
              </h1>
              <p className='avs-hero-body'>
                We audit what ChatGPT, Claude, Perplexity, and Gemini say about
                you, fix what they get wrong, and build the signals that get you
                cited. Whether that means optimizing your current site or
                rebuilding it AI-native, we do the work. Not for sites under 5K
                visits (fix traditional SEO first) or teams that need results in
                2 weeks (citation changes take 4-8 weeks minimum).
              </p>
              <p className='avs-hero-support'>
                Done-for-you GEO, AEO, and LLM optimization, powered by Radar.
              </p>
              <div className='avs-hero-actions'>
                <a className='avs-btn avs-btn-primary' href='/contact-us'>
                  Get it done for you <span className='avs-arrow'>→</span>
                </a>
                <a className='avs-btn avs-btn-secondary' href='/platform'>
                  Or DIY with Radar (free)
                </a>
              </div>
              {heroPrice ? (
                <p className='avs-label avs-hero-price'>{heroPrice}</p>
              ) : null}
            </div>

            <aside
              className='avs-radar-console avs-reveal avs-visible'
              aria-label='Radar monitoring animation'
            >
              <div className='avs-radar-head'>
                <p className='avs-label'>Radar · live signal scan</p>
                <span className='avs-label'>Scanning</span>
              </div>
              <div className='avs-radar-frame'>
                <svg
                  viewBox='0 0 240 240'
                  className='avs-radar-svg'
                  role='img'
                  aria-label='Animated radar scanning ChatGPT, Claude, Perplexity, and Gemini for mentions of your brand'
                >
                  <defs>
                    <linearGradient
                      id='pm-radar-wedge'
                      x1='0'
                      y1='0'
                      x2='1'
                      y2='0'
                    >
                      <stop offset='0%' stopColor='#008dcc' stopOpacity='0' />
                      <stop
                        offset='100%'
                        stopColor='#008dcc'
                        stopOpacity='0.35'
                      />
                    </linearGradient>
                  </defs>
                  <g
                    style={{ stroke: 'var(--s-diagram-ring)' }}
                    strokeWidth='1'
                    fill='none'
                  >
                    <circle cx='120' cy='120' r='104' />
                    <circle cx='120' cy='120' r='72' />
                    <circle cx='120' cy='120' r='40' />
                    <line x1='120' y1='16' x2='120' y2='224' opacity='0.5' />
                    <line x1='16' y1='120' x2='224' y2='120' opacity='0.5' />
                  </g>
                  <g className='avs-pm-radar-sweep'>
                    <path
                      d='M120,120 L120,16 A104,104 0 0 0 34.8,60.3 Z'
                      fill='url(#pm-radar-wedge)'
                    />
                    <line
                      x1='120'
                      y1='120'
                      x2='120'
                      y2='16'
                      stroke='#008dcc'
                      strokeWidth='1.5'
                      strokeOpacity='0.85'
                    />
                  </g>
                  <circle
                    cx='162'
                    cy='82'
                    r='4'
                    fill='#008dcc'
                    className='avs-pm-radar-blip'
                    style={{ animationDelay: '0s' }}
                  />
                  <circle
                    cx='78'
                    cy='96'
                    r='4'
                    fill='#008dcc'
                    className='avs-pm-radar-blip'
                    style={{ animationDelay: '.6s' }}
                  />
                  <circle
                    cx='96'
                    cy='166'
                    r='4'
                    fill='#008dcc'
                    className='avs-pm-radar-blip'
                    style={{ animationDelay: '1.2s' }}
                  />
                  <circle
                    cx='166'
                    cy='150'
                    r='4'
                    fill='#008dcc'
                    className='avs-pm-radar-blip'
                    style={{ animationDelay: '1.8s' }}
                  />
                  <circle
                    cx='120'
                    cy='120'
                    r='7'
                    fill='none'
                    stroke='#008dcc'
                    strokeOpacity='.4'
                  />
                  <circle cx='120' cy='120' r='3.5' fill='#008dcc' />
                </svg>
              </div>
              <p className='avs-radar-caption'>
                Radar tracks what AI engines say about your brand, in real time.
              </p>
              <div className='avs-radar-legend' aria-label='AI engines scanned'>
                <span>
                  <i></i>ChatGPT
                </span>
                <span>
                  <i></i>Claude
                </span>
                <span>
                  <i></i>Perplexity
                </span>
                <span>
                  <i></i>Gemini
                </span>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Lifted out of the hero on Lloyd's instruction (2026-08-13): the
            context paragraph and the three proof stats read as their own band
            below the hero rather than as hero overflow. */}
      <section className='avs-context-bar'>
        <div className='avs-wrap'>
          <p className='avs-hero-context avs-reveal avs-visible'>
            If ChatGPT, Claude, Perplexity, or Gemini do not mention your brand
            when someone asks about your category, you are losing discovery you
            cannot get back with traditional SEO. We audit what AI models see,
            fix what they get wrong, and build the technical signals (structured
            data, llms.txt, speakable schema) that get you cited. We built Radar
            to solve this for ourselves first. Comparing providers first? See
            how we stack up against the{' '}
            <a href='/best-ai-visibility-agencies'>
              best AI visibility agencies
            </a>
            .
          </p>
          <div className='avs-stats avs-reveal avs-visible'>
            <article className='avs-stat'>
              <strong>13 tools</strong>
              <p className='avs-label'>Radar audit</p>
              <p>
                Crawl, robots, llms.txt, readiness, citations, Reddit, AEO,
                answer engine, source influence, SOV, schema, hallucination
                detection.
              </p>
            </article>
            <article className='avs-stat'>
              <strong>5 LLMs</strong>
              <p className='avs-label'>Queried directly</p>
              <p>
                ChatGPT, Claude, Perplexity, and Gemini scored for brand
                mentions, citations, and factual accuracy, plus xAI Grok
                report-only.
              </p>
            </article>
            <article className='avs-stat'>
              <strong>6 weeks</strong>
              <p className='avs-label'>Sprint delivery</p>
              <p>
                Full audit, implementation, and a 60-day action plan delivered.
                You see measurable improvement within the sprint window.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='avs-reader' aria-labelledby='reader-title'>
        <div className='avs-reader-grid'>
          <div className='avs-reader-copy avs-reveal avs-visible'>
            <p className='avs-label avs-label-pink'>Extraction diagnostic</p>
            <h2 className='avs-display' id='reader-title'>
              How AI reads your site
            </h2>
            <p>
              Humans skim. AI extracts structure. Build for the wrong reader and
              you stay invisible.
            </p>
          </div>
          <div className='avs-reader-board avs-reveal avs-visible'>
            <div className='avs-reader-lanes'>
              <div className='avs-lane avs-human'>
                <div className='avs-lane-name'>
                  <strong>A human visitor</strong>
                  <span>Interprets</span>
                </div>
                <div className='avs-lane-items'>
                  <div className='avs-lane-item'>
                    <i>01</i>Sees layout and images
                  </div>
                  <div className='avs-lane-item'>
                    <i>02</i>Reads prose top to bottom
                  </div>
                  <div className='avs-lane-item'>
                    <i>03</i>Infers what you do
                  </div>
                </div>
              </div>
              <div className='avs-lane avs-ai'>
                <div className='avs-lane-name'>
                  <strong>An AI engine</strong>
                  <span>Extracts</span>
                </div>
                <div className='avs-lane-items'>
                  <div className='avs-lane-item'>
                    <i>01</i>Parses structured data (JSON-LD)
                  </div>
                  <div className='avs-lane-item'>
                    <i>02</i>Reads llms.txt for ground truth
                  </div>
                  <div className='avs-lane-item'>
                    <i>03</i>Extracts answer-first blocks and tables
                  </div>
                  <div className='avs-lane-item'>
                    <i>04</i>Cites only what it can extract
                  </div>
                </div>
              </div>
            </div>
            <div className='avs-gap-note'>
              <strong>The gap:</strong> with no structured data, no llms.txt,
              and no answer-first formatting, there is nothing for AI to
              extract. We build the layer AI actually reads.
            </div>
          </div>
        </div>
      </section>

      <section className='avs-problem' id='problem'>
        <div className='avs-wrap avs-problem-grid'>
          <div className='avs-problem-copy avs-reveal avs-visible'>
            <p className='avs-label'>The problem</p>
            <h2 className='avs-display'>
              Your website talks to humans. AI needs a different language.
            </h2>
            <p>
              Traditional SEO optimizes for Google crawlers and human readers.
              AI search engines need structured data, llms.txt files, speakable
              schema, answer-first content, and explicit entity signals. Most
              websites have none of this.
            </p>
            <div className='avs-problem-bullets'>
              <div className='avs-problem-bullet'>
                <i>01</i>
                <span>
                  Missing structured data means AI models guess about your brand
                  (and guess wrong).
                </span>
              </div>
              <div className='avs-problem-bullet'>
                <i>02</i>
                <span>
                  No llms.txt means LLMs have no authoritative source to
                  reference about your company.
                </span>
              </div>
              <div className='avs-problem-bullet'>
                <i>03</i>
                <span>
                  Prose-heavy content without tables, FAQs, or StatBlocks is
                  invisible to AI extraction.
                </span>
              </div>
            </div>
          </div>
          <div className='avs-missing-panel avs-reveal avs-visible'>
            <div className='avs-missing-head'>
              <p className='avs-label'>What most sites are missing</p>
              <span className='avs-label'>5 signals absent</span>
            </div>
            <div className='avs-missing-row'>
              <i>01</i>
              <strong>Structured data</strong>
              <span>Missing</span>
            </div>
            <div className='avs-missing-row'>
              <i>02</i>
              <strong>llms.txt file</strong>
              <span>Missing</span>
            </div>
            <div className='avs-missing-row'>
              <i>03</i>
              <strong>Speakable schema</strong>
              <span>Missing</span>
            </div>
            <div className='avs-missing-row'>
              <i>04</i>
              <strong>Answer-first content</strong>
              <span>Missing</span>
            </div>
            <div className='avs-missing-row'>
              <i>05</i>
              <strong>Entity signals</strong>
              <span>Missing</span>
            </div>
            <div className='avs-missing-close'>
              With none of these, AI fills the gaps by guessing. Usually wrong.
            </div>
          </div>
        </div>
      </section>

      <section className='avs-paths' aria-labelledby='paths-title'>
        <div className='avs-wrap'>
          <div className='avs-paths-head avs-reveal avs-visible'>
            <div>
              <p className='avs-label avs-label-pink'>Decision path</p>
              <h2 className='avs-display' id='paths-title'>
                Two ways we get you cited
              </h2>
            </div>
            <p>
              We do the work either way. Where you start depends on the site you
              have.
            </p>
          </div>
          <div className='avs-start-prompt avs-reveal avs-visible'>
            <strong>Where are you starting?</strong>
            <span>Choose a path</span>
          </div>
          <div className='avs-path-grid avs-reveal avs-visible'>
            <article className='avs-path-card'>
              <div>
                <p className='avs-label'>Existing site</p>
                <h3>Optimize what you have</h3>
                <div className='avs-path-lines'>
                  <div className='avs-path-line'>
                    Audit and fix your existing site
                  </div>
                  <div className='avs-path-line'>
                    Structured data, llms.txt, AEO content restructuring
                  </div>
                </div>
              </div>
              <div className='avs-path-foot'>
                <strong>Visibility Sprint, $4,500, 6 weeks</strong>
                <a className='avs-btn' href='/contact-us'>
                  Book the sprint <span className='avs-arrow'>↗</span>
                </a>
              </div>
            </article>
            <article className='avs-path-card'>
              <div>
                <p className='avs-label'>New foundation</p>
                <h3>Build it AI-native</h3>
                <div className='avs-path-lines'>
                  <div className='avs-path-line'>
                    Full rebuild, AI-readable from the ground up
                  </div>
                  <div className='avs-path-line'>
                    Powered by Radar, with Vector and Hive as optional layers
                  </div>
                </div>
              </div>
              <div className='avs-path-foot'>
                <strong>Scoped per project</strong>
                <a className='avs-btn' href='/contact-us'>
                  Book a strategy call <span className='avs-arrow'>↗</span>
                </a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='avs-pricing' id='packages'>
        <div className='avs-wrap'>
          <div className='avs-pricing-head avs-reveal avs-visible'>
            <div>
              <p className='avs-label avs-label-pink'>Commercial model</p>
              <h2 className='avs-display'>Pricing & Packages</h2>
            </div>
            <p>
              Choose the package that fits your needs. All packages include
              post-launch support.
            </p>
          </div>
          <div className='avs-package-grid avs-reveal avs-visible'>
            <article className='avs-package'>
              <div className='avs-package-head'>
                <span className='avs-package-index'>01 / Starter</span>
                <h3>Starter</h3>
                <p className='avs-package-price'>$4,500</p>
                <p className='avs-package-desc'>
                  Baseline audit and initial implementation
                </p>
              </div>
              <ul className='avs-package-list'>
                <li>Full Radar audit (13 tools) with expert analysis</li>
                <li>
                  Structured data overhaul (Organization, Article, FAQPage,
                  Speakable)
                </li>
                <li>llms.txt creation or optimization</li>
                <li>Knowledge graph entity mapping</li>
                <li>AI bot access configuration (robots.txt)</li>
                <li>Disambiguation strategy (if brand confusion detected)</li>
                <li>Content restructuring for AEO (answer-first format)</li>
                <li>Competitive AI visibility benchmarking</li>
                <li>60-day action plan with prioritized fixes</li>
              </ul>
              <div className='avs-package-time'>
                <span>Estimated timeline</span>
                <strong>4-6 weeks</strong>
              </div>
            </article>
            <article className='avs-package'>
              <div className='avs-package-head'>
                <span className='avs-package-index'>02 / Growth</span>
                <h3>Growth</h3>
                <p className='avs-package-price'>$2,000-$3,500/mo</p>
                <p className='avs-package-desc'>
                  Ongoing AI visibility monitoring and optimization
                </p>
              </div>
              <ul className='avs-package-list'>
                <li>Monthly Radar audits with trend tracking</li>
                <li>Citation monitoring across 4 AI providers</li>
                <li>Hallucination detection and correction</li>
                <li>Schema and structured data maintenance</li>
                <li>Content optimization recommendations (AEO format)</li>
                <li>Competitive visibility tracking</li>
                <li>Monthly strategy report with action items</li>
                <li>Priority support</li>
              </ul>
              <div className='avs-package-time'>
                <span>Estimated timeline</span>
                <strong>Ongoing monthly</strong>
              </div>
            </article>
            <article className='avs-package'>
              <div className='avs-package-head'>
                <span className='avs-package-index'>03 / Scale</span>
                <h3>Scale</h3>
                <p className='avs-package-price'>$5,000-$10,000/mo</p>
                <p className='avs-package-desc'>
                  Multi-domain brands needing full AI search coverage
                </p>
              </div>
              <ul className='avs-package-list'>
                <li>Everything in Visibility Retainer</li>
                <li>Multi-domain audit and monitoring</li>
                <li>Custom knowledge graph development</li>
                <li>Wikidata entity creation and management</li>
                <li>AI content strategy (comparison posts, FAQ content)</li>
                <li>Share of voice tracking and competitive intelligence</li>
                <li>Webhook integrations for real-time alerts</li>
                <li>Quarterly strategy review with leadership</li>
              </ul>
              <div className='avs-package-time'>
                <span>Estimated timeline</span>
                <strong>Ongoing monthly</strong>
              </div>
            </article>
          </div>

          <div className='avs-compare avs-reveal avs-visible'>
            <div className='avs-compare-intro'>
              <p className='avs-label avs-label-pink'>Agency comparison</p>
              <h3>Why Pixelmojo?</h3>
              <p>Compare traditional agencies vs our AI-powered approach</p>
            </div>
            <div className='avs-compare-table'>
              <div className='avs-compare-col'>
                <div className='avs-compare-title'>Traditional Agency</div>
                <div className='avs-compare-row'>6-12 month timelines</div>
                <div className='avs-compare-row'>$50K-$200K+ budgets</div>
                <div className='avs-compare-row'>Fixed deliverables</div>
                <div className='avs-compare-row'>Limited revisions</div>
                <div className='avs-compare-row'>Handoff and done</div>
                <div className='avs-compare-row'>No ongoing optimization</div>
              </div>
              <div className='avs-compare-col'>
                <div className='avs-compare-title'>Pixelmojo (AI-Powered)</div>
                <div className='avs-compare-row'>4-12 week delivery</div>
                <div className='avs-compare-row'>$1,995-$49,995 budgets</div>
                <div className='avs-compare-row'>Flexible and iterative</div>
                <div className='avs-compare-row'>Collaborative process</div>
                <div className='avs-compare-row'>
                  Post-launch support included
                </div>
                <div className='avs-compare-row'>Built for optimization</div>
              </div>
            </div>
          </div>

          <div className='avs-call-band avs-reveal'>
            <div className='avs-call-main'>
              <div>
                <p className='avs-label avs-label-pink'>
                  Free 30-Min Strategy Call
                </p>
                <h3>Let's talk about your project</h3>
                <p>
                  We'll review your needs, discuss options, and create a custom
                  proposal. No pressure, no obligation.
                </p>
              </div>
              <a className='avs-btn avs-btn-primary' href='/contact-us'>
                Book your free call <span className='avs-arrow'>→</span>
              </a>
            </div>
            <aside className='avs-call-local'>
              <h4>🇵🇭 Philippines Startup Special</h4>
              <p className='avs-discount'>15-20% off</p>
              <p>
                We're based in the Philippines and want to support local
                startups. Book a call to see if you qualify.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section className='avs-audit' aria-labelledby='audit-title'>
        <div className='avs-wrap'>
          <div className='avs-audit-head avs-reveal'>
            <div>
              <p className='avs-label'>Audit engine</p>
              <h2 className='avs-display' id='audit-title'>
                The 13-tool Radar audit
              </h2>
            </div>
            <p>
              The same engine behind our free tools runs your full audit. 6 free
              readiness tools plus 7 LLM-powered tools.
            </p>
          </div>
          <div className='avs-audit-ledger avs-reveal'>
            <div className='avs-tool-layer'>
              <div className='avs-layer-head'>
                <h3>Free readiness layer</h3>
                <strong>6</strong>
              </div>
              <div className='avs-tool-row'>
                <i>01</i>
                <span>AI bot crawl check</span>
                <small>Technical</small>
              </div>
              <div className='avs-tool-row'>
                <i>02</i>
                <span>robots.txt analyzer</span>
                <small>Technical</small>
              </div>
              <div className='avs-tool-row'>
                <i>03</i>
                <span>llms.txt validator</span>
                <small>Technical</small>
              </div>
              <div className='avs-tool-row'>
                <i>04</i>
                <span>Schema markup audit</span>
                <small>Technical</small>
              </div>
              <div className='avs-tool-row'>
                <i>05</i>
                <span>AEO page auditor</span>
                <small>Technical</small>
              </div>
              <div className='avs-tool-row'>
                <i>06</i>
                <span>AI Readiness score</span>
                <small>Technical</small>
              </div>
            </div>
            <div className='avs-tool-layer'>
              <div className='avs-layer-head'>
                <h3>LLM-powered layer</h3>
                <strong>7</strong>
              </div>
              <div className='avs-tool-row'>
                <i>07</i>
                <span>Citation tracker</span>
                <small>Paid</small>
              </div>
              <div className='avs-tool-row'>
                <i>08</i>
                <span>Reddit brand monitor</span>
                <small>Paid</small>
              </div>
              <div className='avs-tool-row'>
                <i>09</i>
                <span>Hallucination detection</span>
                <small>Paid</small>
              </div>
              <div className='avs-tool-row'>
                <i>10</i>
                <span>Prompt share of voice</span>
                <small>Paid</small>
              </div>
              <div className='avs-tool-row'>
                <i>11</i>
                <span>Source influence map</span>
                <small>Paid</small>
              </div>
              <div className='avs-tool-row'>
                <i>12</i>
                <span>Answer engine citation testing</span>
                <small>Paid</small>
              </div>
              <div className='avs-tool-row'>
                <i>13</i>
                <span>Brand disambiguation check</span>
                <small>Paid</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className='avs-sprint' id='sprint'>
        <div className='avs-wrap'>
          <div className='avs-sprint-head avs-reveal'>
            <div>
              <p className='avs-label avs-label-pink'>Sprint scope</p>
              <h2 className='avs-display'>What we build in the first sprint</h2>
            </div>
            <p>
              Three layers of AI visibility: technical foundation, content
              optimization, and ongoing monitoring.
            </p>
          </div>
          <div className='avs-layers avs-reveal'>
            <article className='avs-scope-layer'>
              <div className='avs-scope-name'>
                <span>Technical Foundation</span>
                <h3>Make your site AI-readable</h3>
              </div>
              <div className='avs-scope-items'>
                <div className='avs-scope-item'>
                  Structured data overhaul: Organization, Article, FAQPage,
                  SpeakableSpecification, BreadcrumbList with @id references.
                </div>
                <div className='avs-scope-item'>
                  llms.txt creation or optimization following the emerging spec,
                  with entity definitions and use policy.
                </div>
                <div className='avs-scope-item'>
                  robots.txt configuration for 16 AI bots: allow crawlers, block
                  training bots, set clear access policies.
                </div>
              </div>
            </article>
            <article className='avs-scope-layer'>
              <div className='avs-scope-name'>
                <span>Content Optimization</span>
                <h3>Content that AI engines cite</h3>
              </div>
              <div className='avs-scope-items'>
                <div className='avs-scope-item'>
                  Answer-first content restructuring: front-load definitions
                  under each heading so AI can extract standalone answers.
                </div>
                <div className='avs-scope-item'>
                  Comparison tables (HTML) for competitive queries. AI models
                  extract tables verbatim into responses.
                </div>
                <div className='avs-scope-item'>
                  FAQ sections with exact question-answer pairs matching common
                  AI prompts for your category.
                </div>
              </div>
            </article>
            <article className='avs-scope-layer'>
              <div className='avs-scope-name'>
                <span>Monitoring and Correction</span>
                <h3>Track and fix what AI says about you</h3>
              </div>
              <div className='avs-scope-items'>
                <div className='avs-scope-item'>
                  Citation monitoring across ChatGPT, Claude, Perplexity, and
                  Gemini with weekly reporting.
                </div>
                <div className='avs-scope-item'>
                  Hallucination detection: find and correct wrong facts AI
                  states about your brand (pricing, products, location).
                </div>
                <div className='avs-scope-item'>
                  Brand disambiguation when AI confuses you with similarly-named
                  companies.
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='avs-delivery' aria-labelledby='delivery-title'>
        <div className='avs-wrap'>
          <div className='avs-delivery-head avs-reveal'>
            <div>
              <p className='avs-label avs-label-pink'>6-week delivery</p>
              <h2 className='avs-display' id='delivery-title'>
                Done for you, start to finish
              </h2>
            </div>
            <p>Six weeks from audit to monitoring. We run every step.</p>
          </div>
          <div className='avs-timeline avs-reveal'>
            <article className='avs-timeline-step'>
              <span>Audit · Week 1</span>
              <h3>Audit</h3>
              <p>Full 13-tool Radar audit on you and 3 competitors</p>
            </article>
            <article className='avs-timeline-step'>
              <span>Build · Weeks 2-4</span>
              <h3>Build</h3>
              <p>
                We implement structured data, llms.txt, robots.txt, and AEO
                content
              </p>
            </article>
            <article className='avs-timeline-step'>
              <span>Monitor · Weeks 5-6</span>
              <h3>Monitor</h3>
              <p>
                Citation monitoring set up and a 60-day action plan handed off
              </p>
            </article>
          </div>
          <div className='avs-stack avs-reveal'>
            <div className='avs-stack-head'>
              <div>
                <p className='avs-label avs-label-pink'>Product architecture</p>
                <h3>Powered by the stack</h3>
              </div>
              <p>
                Radar is the engine. Vector and Hive are optional layers. Not
                every site needs them.
              </p>
            </div>
            <div className='avs-stack-grid'>
              <a className='avs-stack-link' href='/platform'>
                <div className='avs-stack-meta'>
                  <span>Radar</span>
                  <span>Core · The engine</span>
                </div>
                <div>
                  <h4>Radar</h4>
                  <p>
                    Audits and monitors what AI says about you. Included in
                    every engagement.
                  </p>
                </div>
              </a>
              <a className='avs-stack-link' href='/vector'>
                <div className='avs-stack-meta'>
                  <span>Vector</span>
                  <span>Optional · Optional layer</span>
                </div>
                <div>
                  <h4>Vector</h4>
                  <p>
                    An AI agent that qualifies inbound leads on your site in
                    real time.
                  </p>
                </div>
              </a>
              <a className='avs-stack-link' href='/hive'>
                <div className='avs-stack-meta'>
                  <span>Hive</span>
                  <span>Optional · Optional layer</span>
                </div>
                <div>
                  <h4>Hive</h4>
                  <p>AI coworkers that run the repetitive follow-up and ops.</p>
                </div>
              </a>
            </div>
            <div className='avs-stack-note'>
              Most sites just need Radar. We add Vector or Hive only when the
              funnel justifies it.
            </div>
          </div>
        </div>
      </section>

      <section className='avs-why'>
        <div className='avs-wrap avs-why-grid'>
          <div className='avs-why-copy avs-reveal'>
            <p className='avs-label'>Why us</p>
            <h2 className='avs-display'>
              We built the tools. We use them daily. We published the
              methodology.
            </h2>
            <p>
              Most GEO agencies sell monitoring dashboards. We built Radar (13
              tools, 6 free and 7 paid), wrote the AI Search Playbook (10-part
              series), and use everything we recommend on our own site. This is
              not theory.
            </p>
            <div className='avs-evidence-list'>
              <div className='avs-evidence'>
                <i>01</i>
                <span>
                  Radar by Pixelmojo: 13-tool AI visibility platform we built
                  and maintain. 6 free, 7 paid.
                </span>
              </div>
              <div className='avs-evidence'>
                <i>02</i>
                <span>
                  10-part AI Search Playbook: published methodology covering
                  GEO, AEO, llms.txt, structured data, and citations.
                </span>
              </div>
              <div className='avs-evidence'>
                <i>03</i>
                <span>
                  Our own site scores 85+ on Radar. We eat our own cooking.
                </span>
              </div>
            </div>
          </div>
          <div className='avs-proof-compare avs-reveal'>
            <div className='avs-proof-line'>
              <span>Most GEO agencies</span>
              <strong>Resell a monitoring dashboard</strong>
            </div>
            <div className='avs-proof-line avs-pixel'>
              <span>Pixelmojo</span>
              <strong>Built Radar, the 13-tool platform</strong>
            </div>
            <div className='avs-proof-line avs-pixel'>
              <span>Pixelmojo</span>
              <strong>Published the 10-part AI Search Playbook</strong>
            </div>
            <div className='avs-proof-line avs-pixel'>
              <span>Pixelmojo</span>
              <strong>Score 85+ on our own site</strong>
            </div>
          </div>
        </div>
      </section>

      <section
        className='avs-deliverables'
        aria-labelledby='deliverables-title'
      >
        <div className='avs-wrap'>
          <div className='avs-deliverables-head avs-reveal'>
            <div>
              <p className='avs-label avs-label-pink'>Sprint handoff</p>
              <h2 className='avs-display' id='deliverables-title'>
                What you walk away with
              </h2>
            </div>
            <p>
              Everything your team needs to maintain and improve AI visibility
              after the sprint.
            </p>
          </div>
          <div className='avs-deliverable-grid avs-reveal'>
            <article className='avs-deliverable'>
              <p className='avs-label'>01 / Report</p>
              <h3>Radar audit report</h3>
              <ul>
                <li>
                  Full 13-tool audit with expert analysis, cross-tool insights,
                  and unified visibility score.
                </li>
                <li>
                  Competitive comparison against 3 competitor domains across all
                  13 dimensions.
                </li>
              </ul>
            </article>
            <article className='avs-deliverable'>
              <p className='avs-label'>02 / Build</p>
              <h3>Implementation package</h3>
              <ul>
                <li>
                  Production-ready structured data (JSON-LD), llms.txt, and
                  robots.txt configuration.
                </li>
                <li>
                  Content optimization guide with page-by-page AEO
                  recommendations.
                </li>
              </ul>
            </article>
            <article className='avs-deliverable'>
              <p className='avs-label'>03 / Next</p>
              <h3>Monitoring and action plan</h3>
              <ul>
                <li>
                  Citation tracking dashboard across 4 scored AI providers plus
                  report-only Grok, with baseline metrics.
                </li>
                <li>
                  Prioritized 60-day action plan ranked by effort and impact for
                  ongoing improvements.
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className='avs-faq' id='faq'>
        <div className='avs-wrap avs-faq-grid'>
          <div className='avs-faq-copy avs-reveal'>
            <p className='avs-label avs-label-pink'>Help center</p>
            <h2 className='avs-display'>Frequently Asked Questions</h2>
            <p>Everything you need to know about working with Pixelmojo.</p>
          </div>
          <div className='avs-faq-list avs-reveal'>
            {faqItems.slice(0, 4).map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{item.question}</summary>
                <div className='avs-answer'>{item.answer}</div>
              </details>
            ))}
            <div
              className={`avs-faq-more${faqOpen ? ' avs-revealed' : ''}`}
              id='faqMore'
            >
              {faqItems.slice(4).map(item => (
                <details key={item.question}>
                  <summary>{item.question}</summary>
                  <div className='avs-answer'>{item.answer}</div>
                </details>
              ))}
            </div>
            {faqItems.length > 4 ? (
              <button
                className='avs-show-more'
                id='showFaq'
                type='button'
                aria-controls='faqMore'
                aria-expanded={faqOpen}
                onClick={() => setFaqOpen(v => !v)}
              >
                {faqOpen
                  ? 'Show fewer ↑'
                  : `Show ${faqItems.length - 4} more ↓`}
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section className='avs-proof' id='proof'>
        <div className='avs-wrap'>
          <div className='avs-proof-head avs-reveal'>
            <p className='avs-label'>Proof, not promises</p>
            <h2 className='avs-display'>Proof you can run yourself</h2>
          </div>
          <div className='avs-proof-grid avs-reveal'>
            <article className='avs-proof-card'>
              <div>
                <span className='avs-proof-number'>85+</span>
                <p>
                  Our own site scores 85+ on Radar. Run the audit on
                  pixelmojo.io and check.
                </p>
              </div>
              <a href='/platform'>
                Run it on Radar <span className='avs-arrow'>↗</span>
              </a>
            </article>
            <article className='avs-proof-card'>
              <div>
                <span className='avs-proof-number'>13</span>
                <p>
                  The Radar AI-visibility platform we built and maintain. 6
                  free, 7 paid.
                </p>
              </div>
              <a href='/platform'>
                Get My Free Snapshot <span className='avs-arrow'>↗</span>
              </a>
            </article>
            <article className='avs-proof-card'>
              <div>
                <span className='avs-proof-number'>10-part</span>
                <p>
                  The AI Search Playbook we published: the full GEO and AEO
                  methodology, not a teaser.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className='avs-final-cta'>
        <div className='avs-final-inner avs-reveal'>
          <h2 className='avs-display'>
            Ready to get cited by AI search engines?
          </h2>
          <p>
            Let us do the work: optimize your current site or rebuild it
            AI-native, powered by Radar. Prefer to DIY? Radar is free to start
            (from $5/audit or Pro Retainer $199/mo).
          </p>
          <div className='avs-final-actions'>
            <a className='avs-btn avs-btn-primary' href='/contact-us'>
              Get it done for you <span className='avs-arrow'>↗</span>
            </a>
            <a className='avs-btn avs-btn-secondary' href='/platform'>
              Or DIY with Radar (free)
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
